/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: Toggle.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Toggle component for boolean state management with smooth
    animations and consistent styling throughout the platform.

 🔧 Notes:
    - Implements toggle switch with smooth animations
    - Supports different sizes and visual states
    - Integrates with Tailwind CSS for consistent styling
    - Provides accessibility features and keyboard navigation
    - Supports custom className and onChange handlers

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { useI18n } from '../../hooks/useI18n';

interface ToggleProps {
  id?: string;
  value: boolean;
  onChange: (value: boolean) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  trueLabel?: string;
  falseLabel?: string;
  hideText?: boolean; // Hide the text label (Yes/No, Enabled/Disabled, etc.)
}

export const Toggle: React.FC<ToggleProps> = ({
  id,
  value,
  onChange,
  label,
  disabled = false,
  className = '',
  trueLabel,
  falseLabel,
  hideText = false
}) => {
  const { t } = useI18n();
  
  // Use custom labels if provided, otherwise use default Yes/No
  const displayLabel = value 
    ? (trueLabel || t('common.yes')) 
    : (falseLabel || t('common.no'));
  
  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      <div className="flex items-center">
        <button
          id={id}
          type="button"
          onClick={() => !disabled && onChange(!value)}
          disabled={disabled}
          className={`
            relative inline-flex h-8 items-center rounded-full border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
            ${value 
              ? 'bg-primary border-primary' 
              : 'bg-card border-border hover:border-primary/50'
            }
            ${!hideText ? 'w-20' : 'w-16'}
            ${disabled 
              ? 'opacity-50 cursor-not-allowed' 
              : 'cursor-pointer'
            }
          `}
        >
          {/* Text label inside toggle - positioned opposite to the circle */}
          {!hideText && (
            <>
              {/* Text for OFF state - shown on the right when circle is on left */}
              <span 
                className={`
                  absolute right-1.5 top-1/2 -translate-y-1/2 text-sm font-medium transition-all duration-200 whitespace-nowrap
                  ${value 
                    ? 'opacity-0 pointer-events-none scale-0' 
                    : 'opacity-100 text-muted-foreground scale-100'
                  }
                `}
                style={{ zIndex: 1 }}
              >
                {falseLabel || t('common.no')}
              </span>
              {/* Text for ON state - shown on the left when circle is on right */}
              <span 
                className={`
                  absolute left-1.5 top-1/2 -translate-y-1/2 text-sm font-medium transition-all duration-200 whitespace-nowrap
                  ${value 
                    ? 'opacity-100 text-white scale-100' 
                    : 'opacity-0 pointer-events-none scale-0'
                  }
                `}
                style={{ zIndex: 1 }}
              >
                {trueLabel || t('common.yes')}
              </span>
            </>
          )}
          {/* Toggle circle */}
          <span
            className={`
              absolute left-0.5 top-1/2 -translate-y-1/2 inline-block h-6 w-6 rounded-full bg-background shadow-lg transition-transform duration-200 flex-shrink-0
              ${value ? (hideText ? 'translate-x-10' : 'translate-x-12') : 'translate-x-0'}
            `}
            style={{ zIndex: 2 }}
          />
        </button>
      </div>
    </div>
  );
};

export default Toggle;
